import { useMemo } from 'react';
import type { TFunction } from 'i18next';
import type { RelatedTableInfo, RelatedTableEdgeType } from '../../../types/db-dictionary.js';

interface RelatedTablesSectionProps {
  /** エッジで繋がる関連テーブル一覧 */
  relatedTables: RelatedTableInfo[];
  /** テーブルクリック時にそのテーブルへ遷移するコールバック */
  onNavigate: (schema: string, table: string) => void;
  /** i18n 翻訳関数 */
  t: TFunction;
}

/**
 * edgeType の表示順
 * @graph-connects none
 */
const EDGE_TYPE_ORDER: RelatedTableEdgeType[] = [
  'REFERENCES',
  'SAME_ENTITY',
  'USES_SHARED_SERVICE',
];

/**
 * edgeType ごとのチップ色
 * @graph-connects none
 */
const EDGE_TYPE_COLOR: Record<RelatedTableEdgeType, string> = {
  REFERENCES: 'bg-[var(--color-info)]/10 text-[var(--color-info)] hover:bg-[var(--color-info)]/20',
  SAME_ENTITY: 'bg-purple-500/10 text-purple-400 hover:bg-purple-500/20',
  USES_SHARED_SERVICE: 'bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20',
};

/** マージ済み関連テーブル（同一テーブルの双方向をまとめる） */
interface MergedTable {
  schema: string;
  table: string;
  qualifiedName: string;
  hasOutgoing: boolean;
  hasIncoming: boolean;
}

/**
 * relatedTables を edgeType → スキーマ → テーブルの順にグルーピングし、
 * 同一テーブルの outgoing / incoming をマージする
 * @graph-connects none
 */
function groupRelatedTables(
  relatedTables: RelatedTableInfo[],
): { edgeType: RelatedTableEdgeType; schemas: { schema: string; tables: MergedTable[] }[] }[] {
  // edgeType -> schema -> qualifiedName -> MergedTable
  const byEdge = new Map<RelatedTableEdgeType, Map<string, Map<string, MergedTable>>>();

  for (const r of relatedTables) {
    const edgeType = r.edgeType;
    const bySchema = byEdge.get(edgeType) ?? new Map<string, Map<string, MergedTable>>();
    byEdge.set(edgeType, bySchema);

    const byTable = bySchema.get(r.schema) ?? new Map<string, MergedTable>();
    bySchema.set(r.schema, byTable);

    const existing = byTable.get(r.qualifiedName) ?? {
      schema: r.schema,
      table: r.table,
      qualifiedName: r.qualifiedName,
      hasOutgoing: false,
      hasIncoming: false,
    };
    if (r.direction === 'outgoing') existing.hasOutgoing = true;
    else existing.hasIncoming = true;
    byTable.set(r.qualifiedName, existing);
  }

  const orderedEdgeTypes = EDGE_TYPE_ORDER.filter((e) => byEdge.has(e));
  return orderedEdgeTypes.map((edgeType) => {
    const bySchema = byEdge.get(edgeType);
    const schemas = bySchema ? [...bySchema.keys()].sort() : [];
    return {
      edgeType,
      schemas: schemas.map((schema) => {
        const tables = [...(bySchema?.get(schema)?.values() ?? [])].sort((a, b) =>
          a.table.localeCompare(b.table),
        );
        return { schema, tables };
      }),
    };
  });
}

/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business テーブル詳細モーダル下部の関連テーブル一覧。FK/同一エンティティ/共通サービスの各 edgeType ごとにスキーマ別でテーブルをリンク表示し、クリックでそのテーブルへ遷移する
 * @graph-connects none
 */
export function RelatedTablesSection({ relatedTables, onNavigate, t }: RelatedTablesSectionProps) {
  const grouped = useMemo(() => groupRelatedTables(relatedTables), [relatedTables]);

  /** 向きの記号と説明を取得 */
  const directionMeta = (m: MergedTable): { symbol: string; title: string } => {
    if (m.hasOutgoing && m.hasIncoming) {
      return { symbol: '⇄', title: t('dbDictionary.related.both') };
    }
    if (m.hasOutgoing) {
      return { symbol: '→', title: t('dbDictionary.related.outgoing') };
    }
    return { symbol: '←', title: t('dbDictionary.related.incoming') };
  };

  return (
    <div className="mt-6 border-t border-[var(--color-border)] pt-4">
      <h3 className="text-sm font-medium text-[var(--color-text-secondary)] mb-3">
        {t('dbDictionary.related.title')}
      </h3>

      {grouped.length === 0 ? (
        <p className="text-xs text-[var(--color-text-muted)]">{t('dbDictionary.related.none')}</p>
      ) : (
        <div className="space-y-4">
          {grouped.map(({ edgeType, schemas }) => (
            <div key={edgeType}>
              <p className="text-xs font-semibold text-[var(--color-text-muted)] mb-2">
                {t(`dbDictionary.related.edgeType.${edgeType}`)}
              </p>
              <div className="space-y-2">
                {schemas.map(({ schema, tables }) => (
                  <div key={schema} className="flex flex-col sm:flex-row sm:items-start gap-1.5">
                    <span className="text-xs text-[var(--color-text-muted)] font-mono shrink-0 sm:w-40 sm:text-right sm:pt-0.5">
                      {schema}
                    </span>
                    <div className="flex flex-wrap gap-1.5">
                      {tables.map((m) => {
                        const dir = directionMeta(m);
                        return (
                          <button
                            key={m.qualifiedName}
                            type="button"
                            title={`${dir.title}: ${m.qualifiedName}`}
                            onClick={() => {
                              onNavigate(m.schema, m.table);
                            }}
                            className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded font-mono transition-colors ${EDGE_TYPE_COLOR[edgeType]}`}
                          >
                            <span aria-hidden className="opacity-70">
                              {dir.symbol}
                            </span>
                            {m.table}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
